import React, { useEffect, useState } from 'react';
import {
  TrendingUp, LayoutGrid, ShieldCheck, Swords, Calendar,
  Target, Handshake, Kanban, Radar,
} from 'lucide-react';

/*
 * VendorsTabs — horizontal tab strip for the /vendors page. Mirrors the
 * GAS VendorAnalysisView tab anatomy (App.jsx ALL_TABS): nine tabs in
 * the GAS order with the GAS labels. Hash-synced (#tab=<key>) using the
 * same pattern as Accounts/AccountsTabs.js so links and reload preserve
 * the active tab.
 */

export const VENDOR_TABS = [
  { key: 'trajectory', label: 'Trajectory', icon: TrendingUp },
  { key: 'heatmap', label: 'Heatmap', icon: LayoutGrid },
  { key: 'security', label: 'Security', icon: ShieldCheck },
  { key: 'competitive', label: 'Competitive', icon: Swords },
  { key: 'renewals', label: 'Renewals', icon: Calendar },
  { key: 'takeout', label: 'Take-out', icon: Target },
  { key: 'partners', label: 'Partners', icon: Handshake },
  { key: 'plan', label: '30-60-90', icon: Kanban },
  { key: 'initiatives', label: 'Initiatives', icon: Radar },
];

const HASH_PREFIX = '#tab=';

function readHash() {
  if (typeof window === 'undefined') return VENDOR_TABS[0].key;
  const h = window.location.hash;
  if (!h.startsWith(HASH_PREFIX)) return VENDOR_TABS[0].key;
  const candidate = h.slice(HASH_PREFIX.length);
  return VENDOR_TABS.find(t => t.key === candidate) ? candidate : VENDOR_TABS[0].key;
}

export function useVendorsTabs() {
  const [activeKey, setActiveKey] = useState(readHash);

  // Listen for hash changes (back/forward navigation, link clicks).
  useEffect(() => {
    const onHash = () => setActiveKey(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const setTab = (key) => {
    if (!VENDOR_TABS.find(t => t.key === key)) return;
    setActiveKey(key);
    // replaceState rather than location.hash so tab clicks don't
    // pollute the history stack (same rationale as AccountsTabs).
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = `${HASH_PREFIX}${key}`;
      window.history.replaceState({}, '', url);
    }
  };

  return [activeKey, setTab];
}

export function VendorsTabs({ activeKey, onChange }) {
  return (
    <div className="vendors-tabs" role="tablist" aria-label="Vendor sections">
      {VENDOR_TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            className={`vendors-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => onChange(tab.key)}
          >
            <Icon size={14} />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default VendorsTabs;
